/**
 * Chatbot utilities
 */

/**
 * Creates a DirectLine object with custom headers
 * @param {string} directLineToken - The DirectLine token for authentication
 * @param {function} getSecureToken - Function to get the JWT token
 * @param {object} store - The Vue store instance
 * @returns {object} DirectLine object with custom headers
 */
export const createDirectLineWithHeaders = (directLineToken, getSecureToken, store) => {
  try {
    // Create the standard DirectLine object
    const directLine = window.WebChat.createDirectLine({
      token: directLineToken,
      webSocket: true // Use WebSocket if possible for better performance
    });
    
    // Create a wrapper with the same interface as DirectLine
    return {
      activity$: directLine.activity$,
      connectionStatus$: directLine.connectionStatus$,
      
      // Override the postActivity method to add custom headers
      postActivity: activity => {
        try {
          // Add your custom headers to every activity
          const activityWithHeaders = {
            ...activity,
            channelData: {
              ...activity.channelData,
              headers: {
                'x-jwt-token': getSecureToken() || '',
              },
              params: {
                ...(store.state.client && { 'clientId': store.state.client }),
                ...(store.state.chatbot.directLineTenant && { 'tenantId': store.state.chatbot.directLineTenant }),
                ...(store.state.chatbot.project && { 'projectId': store.state.chatbot.project }),
                ...(store.state.chatbot.task && { 'taskId': store.state.chatbot.task }),
                ...(store.state.chatbot.filters && { 'filters': store.state.chatbot.filters }),
                ...(store.state.chatbot.role && { 'role': store.state.chatbot.role }),
              }
            }
          };
          
          // Pass the modified activity to the original postActivity method
          return directLine.postActivity(activityWithHeaders)
            .catch(error => {
              console.error('Error posting activity:', error);
              throw error;
            });
        } catch (error) {
          console.error('Error in postActivity:', error);
          throw error;
        }
      }
    };
  } catch (error) {
    console.error('Error creating DirectLine with headers:', error);
    // Return a dummy DirectLine object that does nothing
    return {
      activity$: { subscribe: () => ({ unsubscribe: () => {} }) },
      connectionStatus$: { subscribe: () => ({ unsubscribe: () => {} }) },
      postActivity: () => Promise.reject(new Error('Chat not initialized'))
    };
  }
};

/**
 * Load the WebChat script dynamically
 * @param {function} callback - Function to call when script is loaded
 * @returns {HTMLScriptElement} The created script element
 */
export const loadWebChatScript = (callback) => {
  const script = document.createElement('script');
  script.src = 'https://cdn.botframework.com/botframework-webchat/latest/webchat.js';
  script.async = true;
  script.onload = callback;
  document.head.appendChild(script);
  return script;
};
